import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface OrderItem {
  product_id: number;
  name: string;
  unit_price: number;
  quantity: number;
  image: string;
}

interface Order {
  id: number;
  stripe_session_id: string | null;
  customer_email: string;
  customer_name: string | null;
  customer_phone: string | null;
  shipping_address: string | null;
  shipping_city: string | null;
  shipping_postal_code: string | null;
  shipping_country: string | null;
  status: string;
  total_amount: number;
  currency: string;
  items: OrderItem[];
  paid: boolean;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  preparing: 'Preparando',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  paid: 'bg-brand-sage/30 text-brand-black',
  preparing: 'bg-sky-50 text-sky-700',
  shipped: 'bg-brand-gray-100 text-brand-gray-600',
  delivered: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-600',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'paid' | 'preparing' | 'shipped' | 'delivered' | 'cancelled'>('all');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [adminNote, setAdminNote] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error(error);
    } else {
      setOrders((data as Order[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (id: number, status: string) => {
    await supabase.from('orders').update({ status }).eq('id', id);
    fetchOrders();
  };

  const updateNotes = async (id: number) => {
    await supabase.from('orders').update({ notes: adminNote }).eq('id', id);
    fetchOrders();
    setDetailId(null);
    setAdminNote('');
  };

  const filtered = filter === 'all'
    ? orders
    : orders.filter((o) => o.status === filter);

  const detailOrder = orders.find((o) => o.id === detailId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-serif text-brand-black text-2xl font-medium">
          Pedidos <span className="text-brand-gray-400">({orders.length})</span>
        </h2>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {(['all', 'pending', 'paid', 'preparing', 'shipped', 'delivered', 'cancelled'] as const).map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full font-sans text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                filter === key
                  ? 'bg-brand-black text-white'
                  : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              {key === 'all' ? 'Todos' : statusLabels[key]}
            </button>
          ))}
          <button
            onClick={fetchOrders}
            className="px-3 py-1.5 rounded-full bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black font-sans text-xs uppercase tracking-wider whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />
            Recargar
          </button>
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-10 text-center">
          <i className="ri-loader-4-line animate-spin text-brand-gray-400 text-xl" />
          <p className="font-sans text-brand-gray-400 text-sm mt-2">Cargando pedidos...</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-10 text-center">
          <p className="font-sans text-brand-gray-400 text-sm">No hay pedidos en esta categoría.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-brand-gray-50">
                <tr>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Pedido</th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Cliente</th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Total</th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Estado</th>
                  <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Fecha</th>
                  <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-gray-200">
                {filtered.map((order) => (
                  <tr key={order.id} className="hover:bg-brand-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="font-sans text-brand-black text-sm font-medium">#{order.id}</p>
                      <p className="font-sans text-brand-gray-400 text-xs">{order.items.length} artículo(s)</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-sans text-brand-black text-sm">{order.customer_name || '—'}</p>
                      <p className="font-sans text-brand-gray-400 text-xs">{order.customer_email}</p>
                      {order.customer_phone && (
                        <p className="font-sans text-brand-gray-400 text-xs">{order.customer_phone}</p>
                      )}
                    </td>
                    <td className="px-5 py-4 font-sans text-brand-black text-sm font-semibold">
                      {(order.total_amount / 100).toLocaleString('es-ES', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${statusStyles[order.status] || 'bg-brand-gray-100 text-brand-gray-500'}`}>
                        {statusLabels[order.status] || order.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-sans text-brand-gray-500 text-sm">
                      {new Date(order.created_at).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => { setDetailId(order.id); setAdminNote(order.notes || ''); }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Ver detalle"
                      >
                        <i className="ri-eye-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-brand-black text-xl">Pedido #{detailOrder.id}</h3>
              <button
                onClick={() => { setDetailId(null); setAdminNote(''); }}
                className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Cliente</label>
                  <p className="font-sans text-brand-black text-sm mt-1">{detailOrder.customer_name || '—'}</p>
                  <p className="font-sans text-brand-gray-400 text-xs">{detailOrder.customer_email}</p>
                  {detailOrder.customer_phone && <p className="font-sans text-brand-gray-400 text-xs">{detailOrder.customer_phone}</p>}
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Dirección de envío</label>
                  <p className="font-sans text-brand-black text-sm mt-1">{detailOrder.shipping_address || '—'}</p>
                  <p className="font-sans text-brand-gray-400 text-xs">
                    {detailOrder.shipping_city} {detailOrder.shipping_postal_code}
                  </p>
                  <p className="font-sans text-brand-gray-400 text-xs">{detailOrder.shipping_country}</p>
                </div>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Artículos</label>
                <div className="mt-2 space-y-3">
                  {detailOrder.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-brand-gray-50 rounded-xl">
                      {item.image && (
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-brand-gray-100 flex-shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-brand-black text-sm font-medium truncate">{item.name}</p>
                        <p className="font-sans text-brand-gray-400 text-xs">Cantidad: {item.quantity}</p>
                      </div>
                      <p className="font-sans text-brand-black text-sm font-semibold">
                        {(item.unit_price * item.quantity).toLocaleString('es-ES', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between font-serif text-brand-black text-lg font-medium pt-2 border-t border-brand-gray-200">
                <span>Total</span>
                <span>
                  {(detailOrder.total_amount / 100).toLocaleString('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Cambiar estado</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(['pending', 'paid', 'preparing', 'shipped', 'delivered', 'cancelled'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(detailOrder.id, s)}
                      className={`px-3 py-1.5 rounded-full font-sans text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                        detailOrder.status === s
                          ? 'bg-brand-black text-white'
                          : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
                      }`}
                    >
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Notas internas</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
                  placeholder="Notas solo visibles para el equipo..."
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => updateNotes(detailOrder.id)}
                    className="bg-brand-black text-white font-sans text-sm px-4 py-2 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
                  >
                    Guardar notas
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}